// Guards date inputs against impossible entries.


// Normal range; everything else is allowlisted, autocorrected, or an easter egg.
export const MIN_DATE = '2010-01-01';
export const MAX_DATE = `${new Date().getFullYear() + 3}-12-31`;
const MIN_YEAR = parseInt(MIN_DATE, 10); // 2010 MIN_DATE
const MAX_YEAR = parseInt(MAX_DATE, 10); // current year + 3 (year-end ceiling)

// Silent allowlist: historical window -- exluded from double entry rule
const HISTORY_MIN_YEAR = 300;
const HISTORY_MAX_YEAR = 610;


// Greek temple eras (BC years, start = older) — architects from Vitruvius, Book VII preface.
const BC_INFRA_RANGES = [
	{ start: 3200, end: 3100, name: 'the Newgrange passage tomb of Sí an Bhrú' },
	{ start: 2580, end: 2540, name: 'the Great Pyramid of Giza, raised for Pharaoh Khufu' },
	{ start: 750, end: 700, name: 'the Great Dam of Marib, the waterworks of Sheba' },
	{ start: 610,  end: 575,  name: 'the Etemenanki ziggurat of Babylon — the Tower of Babel — and the Ishtar Gate of Nebuchadnezzar II' },
	{ start: 570, end: 550, name: 'the Temple of Artemis at Ephesus, raised by Chersiphron and Metagenes, and the Heraion of Theodorus on Samos' },
	{ start: 520, end: 515, name: 'the first Olympieion at Athens, begun by Antistates, Callaeschrus, Antimachides, and Pormus' },
	{ start: 447, end: 432, name: 'the Parthenon of Ictinus and Carpion' },
	{ start: 353,  end: 350,  name: 'the Mausoleum of Halicarnassus, by Satyrus and Pytheos' },
	{ start: 313, end: 300, name: 'the Temple of Apollo at Didyma, by Paeonius the Milesian and Daphnis of Ephesus' },
	{ start: 297,  end: 280,  name: 'the Pharos Lighthouse of Alexandria and the Colossus of Rhodes' },
	{ start: 221,  end: 206,  name: 'the first Great Wall, under Qin Shi Huang' },
	{ start: 200,  end: 190,  name: 'the Temple of Artemis Leukophryene at Magnesia, by Hermogenes' },
	{ start: 174, end: 164, name: 'the Olympieion at Athens, resumed by Cossutius' },
];

// Major historical public infrastructure projects — searchable eras.
const INFRA_RANGES = [
	{ min: 13,   max: 19,   name: 'Fanum Fortunae' },
	{ min: 38,   max: 52,   name: 'the Aqua Claudia and Anio Novus, the great aqueducts of Rome' },
	{ min: 53,   max: 69,   name: 'the lead siphons of the Lyon aqueducts, which dove through river valleys and climbed back out under their own pressure' },
	{ min: 70,   max: 80,   name: 'the Colosseum' },
	{ min: 100,  max: 250,  name: 'the pyramids and the Avenue of the Dead at Teotihuacán' },
	{ min: 404,  max: 413,  name: 'the Theodosian Walls of Constantinople' },
	{ min: 460,  max: 480,  name: 'the rock-cut Buddhist temples of the Ajanta Caves' },
	{ min: 532,  max: 537,  name: 'the Hagia Sophia' },
	{ min: 542,  max: 548,  name: 'the repair of the Great Dam of Marib — a thousand years old and still worth feeding ten thousand workers to save' },
	{ min: 605,  max: 609,  name: 'the Grand Canal of China' },
	{ min: 1163, max: 1345, name: 'Notre-Dame de Paris' },
	{ min: 1817, max: 1825, name: 'the Erie Canal' },
	{ min: 1859, max: 1869, name: 'the Suez Canal and the Transcontinental Railroad' },
	{ min: 1870, max: 1883, name: 'the Brooklyn Bridge' },
	{ min: 2031, max: 3200, name: 'the Dyson swarm of Earth' },
];

// Future infrastructure projects — one per allowed pop-culture year.
const FUTURE_INFRA = {
	2050:  'the Obayashi space elevator',
	2063:  "the Phoenix launch complex at Bozeman, Montana",
	2122:  'the Weyland-Yutani commercial towing network',
	2151:  'the Warp Five Complex',
	2161:  'the founding halls of the United Federation of Planets',
	2179:  "the Hadley's Hope atmosphere processors",
	2199:  'the machine-built power grid of Zion',
	2233:  'the Riverside Shipyard',
	2245:  'the San Francisco Fleet Yards',
	2263:  'Starbase Yorktown',
	2265:  'the Starbase 11 drydocks',
	2269:  'the Memory Alpha archive',
	2285:  'Earth Spacedock',
	2346:  'Terok Nor',
	2364:  'the Utopia Planitia Fleet Yards',
	2369:  'the Dyson sphere in the Relics sector',
	2517:  'the terraforming stations of the border planets',
	2552:  'the orbital defense platforms of Reach',
	2805:  'the Buy n Large waste-allocation towers',
	3000:  'the New New York tube transit system',
	5000:  'the Dyson sphere of the Sun',
	10191: 'the spice harvesters of Arrakis',
};

// Per-field memory of the previous raw entry — same value entered twice is deliberate.
const lastRaw = {};

// Blocking prompts return {block: true}; informational ones just carry a message.
function eggFor(year, month, day, bc) {
	//ANCIENT ERA EASTER EGGS
	if (bc) {
		if (year >= 4000 && year <= 4713)
			return { block: false, message: 'You have reached the era of the Emperor’s Death Star project, in a galaxy far, far away. Certified payroll records for this project are coming soon.' };
		if (year > 4713)
			return { block: true, message: 'Records before 4713 BC are coming soon. Our calendar arithmetic, like the Julian day count, begins there.' };
		if (year >= 250 && year <= 260)
			return { block: false, message: 'You have reached the age of the Dujiangyan irrigation system, built by Li Bing for the state of Qin. Certified payroll records for this project are coming soon.' };
		if (year >= 45 && year <= 713 && (month === '07' || month === '08'))
			return { block: true, message: 'Month not found: in this era July is still called Quintilis and August is Sextilis. The renaming is pending imperial approval.' };
		if (year >= 50 && year <= 58)
			return { block: false, message: 'The master builder is on march with Marcus, Minidius, and Gnaeus, testing construction of machines for Book X; no payroll survives from Alesia. Pause here a moment, then search on.' };
		
		const bcInfra = BC_INFRA_RANGES.find(r => year <= r.start && year >= r.end);
		if (bcInfra)
			return { block: false, message: `You have reached the age of ${bcInfra.name}. Certified payroll records for this project are coming soon.` };
		
		return null;
	}

	//EARLY AGE
	if (year >= 117 && year <= 126)
		return { block: false, message: 'The Pantheon is rebuilt from the ashes of an earlier structure.' };

	//MEDIEVAL ERA EASTER EGGS
	if (year >= 614 && year <= 911)
		return { block: false, message: 'No public works records exist for this period. Scholars disagree on whether the period itself does.' };
	if (year === 1582 && month === '10' && day >= '05' && day <= '14')
		return { block: false, message: 'These dates were removed by papal decree in 1582. No permits were issued.' };
	
	//'Age' EASTER EGGS
	if (FUTURE_INFRA[year])
		return { block: false, message: `You have reached the age of ${FUTURE_INFRA[year]}. Certified payroll records for this project are coming soon.` };
	const infra = INFRA_RANGES.find(r => year >= r.min && year <= r.max);
	if (infra)
		return { block: false, message: `You have reached the age of ${infra.name}. Certified payroll records for this project are coming soon.` };
	
	return null;
}

// Returns { value, message }. `value` is what the field and variable should hold.
export function guardDate(fieldKey, raw, bc = false) {
	if (!raw) { lastRaw[fieldKey] = ''; return { value: '', message: null }; }

	const forced = raw === lastRaw[fieldKey];
	lastRaw[fieldKey] = raw;

	const [y, m, d] = raw.split('-');
	const year = parseInt(y, 10);
	const egg = eggFor(year, m, d, bc);

	// Blocking eggs: show the message but leave the typed date visible and searchable.
	if (egg && egg.block) return { value: raw, message: egg.message };

	// Deliberate re-entry of the same value: accept verbatim.
	if (forced) return { value: raw, message: egg && egg.message };

	// BC dates below the floor already blocked above; the rest pass through.
	if (bc) return { value: raw, message: egg && egg.message };

	// Normal range and the silent historical window pass through on a single entry.
	if ((year >= MIN_YEAR && year <= MAX_YEAR) ||
		(year >= HISTORY_MIN_YEAR && year <= HISTORY_MAX_YEAR))
		return { value: raw, message: egg && egg.message };

	// Stray-digit autocorrect: 5+ digit year whose first four digits are in range.
	if (y.length >= 5) {
		const first4 = parseInt(y.slice(0, 4), 10);
		if (first4 >= MIN_YEAR && first4 <= MAX_YEAR)
			return { value: `${y.slice(0, 4)}-${m}-${d}`, message: null };
	}

	// Otherwise clamp the year only; month and day are kept.
	const clamped = year < MIN_YEAR ? String(MIN_YEAR) : String(MAX_YEAR);
	return { value: `${clamped}-${m}-${d}`, message: null };
}


// Clamp-only guard: no eggs, no allowlists, no re-entry rule.
export function clampDate(raw) {
	if (!raw) return '';
	const [y, m, d] = raw.split('-');
	const year = parseInt(y, 10);
	if (year >= MIN_YEAR && year <= MAX_YEAR) return raw;
	// Stray-digit autocorrect: 5+ digit year whose first four digits are in range.
	if (y.length >= 5) {
		const first4 = parseInt(y.slice(0, 4), 10);
		if (first4 >= MIN_YEAR && first4 <= MAX_YEAR) return `${y.slice(0, 4)}-${m}-${d}`;
	}
	const clamped = year < MIN_YEAR ? String(MIN_YEAR) : String(MAX_YEAR);
	return `${clamped}-${m}-${d}`;
}